import React, { useMemo, useState, useEffect } from 'react';
import VirtualizedItemList from './VirtualizedItemList';

import {
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TableContainer,
  TableHead,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PolygonData, PolygonGroup, AttributeHistoryItem } from '../../types/schema';
import { formatArea, formatIntensity, formatNEE } from '../../utils/formatters';
import { GroupingOption, SortingOption } from '../../types/enums';
import { endpoints } from '../../services/apiClient';
import { useAppSelector } from '../../store/hooks';

const PanelContainer = styled(Paper)(({ theme }) => ({
  height: 'calc(100vh - 250px)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  paddingBottom: theme.spacing(1),
}));

const PanelHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ControlsSection = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ListContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingBottom: 8, // Small padding at bottom for better UX
  minHeight: 0, // Important for flexbox scrolling
});


const ItemInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: '0.65rem',
  color: theme.palette.text.secondary,
  flexWrap: 'nowrap',
  overflow: 'hidden',
  minWidth: 0,
}));

interface CopyableCellProps {
  value: string | number | null | undefined;
}

const CopyableCell: React.FC<CopyableCellProps> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const textToCopy = String(value || '');

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          {textToCopy}
        </Typography>
        <IconButton size="small" onClick={handleCopy} title={copied ? "Copied!" : "Copy"}>
          <ContentCopyIcon fontSize="small" sx={{ color: copied ? 'success.main' : 'action.active' }} />
        </IconButton>
      </Stack>
    </TableCell>
  );
};

interface AttributesTooltipProps {
  polygon: PolygonData;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  onExpand: () => void;
}

const AttributesTooltip: React.FC<AttributesTooltipProps> = ({ polygon, open, onClose, onOpen, onExpand }) => {
  return (
    <Tooltip
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      title={
        <Box
          sx={{ p: 0.5, backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Table size="small" padding="none">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>ID</TableCell>
                <CopyableCell value={polygon.shortCode || polygon.id} />
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>UUID</TableCell>
                <CopyableCell value={polygon.uuid} />
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Name</TableCell>
                <CopyableCell value={polygon.name} />
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Area</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatArea(polygon.area)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>NEE Total</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatNEE(polygon.neeTotal, 'tCO2')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Annual NEE Total</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatNEE(polygon.neePerYear, 'tCO2')}/yr</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>NEE Intensity</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatIntensity(polygon.neeIntensity)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Annual NEE Intensity</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatIntensity(polygon.neeIntensityPerYear)}/yr</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Country</TableCell>
                <CopyableCell value={polygon.country || 'N/A'} />
              </TableRow>
            </TableBody>
          </Table>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
            <Button
              size="small"
              onClick={onExpand}
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              More details
            </Button>
          </Box>
        </Box>
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
            p: 0,
            maxWidth: 'none',
          }
        },
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              bgcolor: 'transparent',
              boxShadow: 'none',
              p: 0,
            },
            '& .MuiTooltip-arrow': {
              color: 'white',
            }
          }
        }
      }}
    >
      <IconButton
        size="small"
        sx={{ p: 0.5 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <HelpOutlineOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

interface PolygonDetailsModalProps {
  polygon: PolygonData | null;
  open: boolean;
  onClose: () => void;
}

const PolygonDetailsModal: React.FC<PolygonDetailsModalProps> = ({ polygon, open, onClose }) => {
  const [history, setHistory] = useState<AttributeHistoryItem[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { selectedAccount } = useAppSelector(state => state.app);

  useEffect(() => {
    if (open && polygon) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const historyRes = await endpoints.polygonAttributesHistory(polygon.id, selectedAccount?.accountId);
          const tagsRes = await endpoints.polygonTags(polygon.id, selectedAccount?.accountId);

          const sortedHistory = historyRes.history.sort((a, b) => {
            if (a.attribute_name < b.attribute_name) {
              return -1;
            }
            if (a.attribute_name > b.attribute_name) {
              return 1;
            }
            return new Date(a.effective_from).getTime() - new Date(b.effective_from).getTime();
          });

          setHistory(sortedHistory);
          setTags(tagsRes.tags);
        } catch (error) {
          console.error("Failed to fetch polygon details", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [open, polygon, selectedAccount]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Details for {polygon?.name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>Tags</Typography>
              {tags.length > 0 ? (
                <Stack direction="row" spacing={1}>
                  {tags.map((tag, index) => <Chip key={index} label={tag} />)}
                </Stack>
              ) : (
                <Typography variant="body2">No tags associated.</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>Attribute History</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Attribute</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Effective From</TableCell>
                      <TableCell>Effective To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.attribute_name}</TableCell>
                        <TableCell>{item.attribute_value}</TableCell>
                        <TableCell>{item.effective_from}</TableCell>
                        <TableCell>{item.effective_to || 'Current'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


interface GroupTooltipProps {
  group: PolygonGroup;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}

const GroupTooltip: React.FC<GroupTooltipProps> = ({ group, open, onClose, onOpen }) => {
  return (
    <Tooltip
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      title={
        <Box sx={{ maxWidth: 300, p: 0.5, backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px' }}>
          <Table size="small" padding="none">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Name</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{group.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Polygons</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{group.polygonCount}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Total Area</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatArea(group.totalArea)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>NEE</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatNEE(group.neeTotal, 'tCO2')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>NEE (per year)</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatNEE(group.neePerYear, 'tCO2')}/yr</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Intensity</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatIntensity(group.neeIntensity)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none', py: 0.5, px: 1 }}>Intensity (per year)</TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 0.5, px: 1 }}>{formatIntensity(group.neeIntensityPerYear)}/yr</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
            p: 0,
            maxWidth: 'none',
          }
        },
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              bgcolor: 'transparent',
              boxShadow: 'none',
              p: 0,
            },
            '& .MuiTooltip-arrow': {
              color: 'white',
            }
          }
        }
      }}
    >
      <IconButton size="small" sx={{ p: 0.5 }}>
        <HelpOutlineOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

interface SelectionPanelProps<T extends { id: number | string }> {
  items: T[];
  selectedIds: (number | string)[];
  onToggle: (id: number | string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  title?: string;
  loading?: boolean;
  renderItem: (item: T, isSelected: boolean, selectionIndex: number) => React.ReactNode;
  renderExtraControls?: () => React.ReactNode;
}

const SelectionPanel = <T extends { id: number | string }>({
  items,
  selectedIds,
  onToggle,
  onSelectAll,
  onDeselectAll,
  title = "Items",
  loading = false,
  renderItem,
  renderExtraControls,
}: SelectionPanelProps<T>) => {
  const isSelected = (id: number | string) => selectedIds.includes(id);

  // Determine if we should show loading indicator
  // Show loading if either explicitly loading OR if status is loading and we have items (transitioning state)
  const isLoading = loading;

  return (
    <PanelContainer>
      {renderExtraControls && renderExtraControls()}

      <PanelHeader>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select items to view and compare
        </Typography>
      </PanelHeader>

      <ControlsSection direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={onSelectAll}
          sx={{ fontSize: '0.75rem' }}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={onDeselectAll}
          sx={{ fontSize: '0.75rem' }}
        >
          Deselect All
        </Button>
      </ControlsSection>

      <ListContainer sx={{ position: 'relative' }}>
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div style={{ height: '100%', overflow: 'hidden' }}>
            {items.length > 0 ? (
              <VirtualizedItemList
                items={items}
                selectedIds={selectedIds}
                isSelected={isSelected}
                onToggle={onToggle}
                renderItem={renderItem}
                height={500}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>No items found.</Typography>
              </Box>
            )}
          </div>
        )}
      </ListContainer>
    </PanelContainer>
  );
};

const PolygonItem: React.FC<{ polygon: PolygonData, isSelected: boolean, selectionIndex: number }> = ({ polygon, isSelected, selectionIndex }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Box sx={{ flex: 1, ml: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {polygon.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isSelected && (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                {selectionIndex}
              </Box>
            )}
            <AttributesTooltip
              polygon={polygon}
              open={tooltipOpen}
              onClose={() => setTooltipOpen(false)}
              onOpen={() => setTooltipOpen(true)}
              onExpand={() => {
                setTooltipOpen(false);
                setModalOpen(true);
              }}
            />
          </Box>
        </Stack>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <ItemInfo>
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }}>
              ID: {polygon.shortCode || polygon.id}
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }}>
              Area: {formatArea(polygon.area)}
            </Typography>
          </ItemInfo>
          <ItemInfo>
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }}>
              Intensity: {formatIntensity(polygon.neeIntensityPerYear)}/yr
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }}>
              Total: {formatNEE(polygon.neePerYear, 'tCO2')}/yr
            </Typography>
          </ItemInfo>
        </Stack>
      </Box>
      <PolygonDetailsModal polygon={polygon} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

const PolygonGroupItem: React.FC<{ group: PolygonGroup, isSelected: boolean, selectionIndex: number }> = ({ group, isSelected, selectionIndex }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <Box sx={{ flex: 1, ml: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {group.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isSelected && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              {selectionIndex}
            </Box>
          )}
          <GroupTooltip
            group={group}
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            onOpen={() => setTooltipOpen(true)}
          />
        </Box>
      </Stack>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        <ItemInfo>
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }} >Polygons: {group.polygonCount}</Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }} >Area: {formatArea(group.totalArea)}</Typography>
        </ItemInfo>
        <ItemInfo>
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }} >Intensity: {formatIntensity(group.neeIntensityPerYear)}/yr</Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="inherit" component="span" noWrap sx={{ minWidth: 0 }} >NEE: {formatNEE(group.neePerYear, 'tCO2')}/yr</Typography>
        </ItemInfo>
      </Stack>
    </Box>
  );
};

interface PolygonSelectionPanelProps {
  groupingOption: GroupingOption;
  onGroupingChange: (option: GroupingOption) => void;
  sortingOption: SortingOption;
  onSortingChange: (option: SortingOption) => void;
  polygons: PolygonData[];
  polygonGroups: PolygonGroup[];
  selectedPolygons: number[];
  selectedGroups: string[];
  onPolygonToggle: (polygonId: number) => void;
  onGroupToggle: (groupId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  title?: string;
  loading?: boolean;
}

const PolygonPanelControls: React.FC<{
  groupingOption: GroupingOption;
  onGroupingChange: (option: GroupingOption) => void;
  sortingOption: SortingOption;
  onSortingChange: (option: SortingOption) => void;
}> = React.memo(({ groupingOption, onGroupingChange, sortingOption, onSortingChange }) => {
  return (
    <ControlsSection direction="row" spacing={2} sx={{ pt: 2, pb: 2, flexWrap: "wrap" }}>
      <FormControl sx={{ minWidth: 150, flexGrow: 1 }}>
        <InputLabel>Grouping</InputLabel>
        <Select
          value={groupingOption}
          label="Grouping"
          onChange={(e) => onGroupingChange(e.target.value as GroupingOption)}
          MenuProps={{
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: '0.7rem',
              },
            },
          }}
          sx={{ '& .MuiSelect-select': { fontSize: '0.7rem' } }}
        >
          <MenuItem value={GroupingOption.NONE}>No grouping</MenuItem>
          <MenuItem value={GroupingOption.COUNTRY}>Country</MenuItem>
          <MenuItem value={GroupingOption.NAME}>Polygon Name</MenuItem>
          <MenuItem value={GroupingOption.LAND_COVER}>Land Cover</MenuItem>
          <MenuItem value={GroupingOption.TAGS}>Tags</MenuItem>
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 150, flexGrow: 1 }}>
        <InputLabel>Sorting</InputLabel>
        <Select
          value={sortingOption}
          label="Sorting"
          onChange={(e) => onSortingChange(e.target.value as SortingOption)}
          MenuProps={{
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: '0.7rem',
              },
            },
          }}
          sx={{ '& .MuiSelect-select': { fontSize: '0.7rem' } }}
        >
          <MenuItem value={SortingOption.NONE}>No sorting</MenuItem>
          <MenuItem value={SortingOption.NAME_ASC}>Name (A-Z)</MenuItem>
          <MenuItem value={SortingOption.NAME_DESC}>Name (Z-A)</MenuItem>
          <MenuItem value={SortingOption.AREA_ASC}>Area (Low-High)</MenuItem>
          <MenuItem value={SortingOption.AREA_DESC}>Area (High-Low)</MenuItem>
          <MenuItem value={SortingOption.NEE_ASC}>NEE (Low-High)</MenuItem>
          <MenuItem value={SortingOption.NEE_DESC}>NEE (High-Low)</MenuItem>
        </Select>
      </FormControl>
    </ControlsSection>
  );
});

const PolygonSelectionPanel: React.FC<PolygonSelectionPanelProps> = ({
  groupingOption,
  onGroupingChange,
  sortingOption,
  onSortingChange,
  polygons,
  polygonGroups,
  selectedPolygons,
  selectedGroups,
  onPolygonToggle,
  onGroupToggle,
  onSelectAll,
  onDeselectAll,
  title,
  loading,
}) => {
  const sortedPolygons = useMemo(() => {
    const sorted = Array.isArray(polygons) ? [...polygons] : [];
    switch (sortingOption) {
      case SortingOption.NAME_ASC:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case SortingOption.NAME_DESC:
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case SortingOption.AREA_ASC:
        return sorted.sort((a, b) => a.area - b.area);
      case SortingOption.AREA_DESC:
        return sorted.sort((a, b) => b.area - a.area);
      case SortingOption.NEE_ASC:
        return sorted.sort((a, b) => a.neeTotal - b.neeTotal);
      case SortingOption.NEE_DESC:
        return sorted.sort((a, b) => b.neeTotal - a.neeTotal);
      default:
        return sorted;
    }
  }, [polygons, sortingOption]);

  const sortedPolygonGroups = useMemo(() => {
    const sorted = Array.isArray(polygonGroups) ? [...polygonGroups] : [];
    switch (sortingOption) {
        case SortingOption.NAME_ASC:
            return sorted.sort((a, b) => a.name.localeCompare(b.name));
        case SortingOption.NAME_DESC:
            return sorted.sort((a, b) => b.name.localeCompare(a.name));
        case SortingOption.AREA_ASC:
            return sorted.sort((a, b) => a.totalArea - b.totalArea);
        case SortingOption.AREA_DESC:
            return sorted.sort((a, b) => b.totalArea - a.totalArea);
        case SortingOption.NEE_ASC:
            return sorted.sort((a, b) => a.neeTotal - b.neeTotal);
        case SortingOption.NEE_DESC:
            return sorted.sort((a, b) => b.neeTotal - a.neeTotal);
        default:
            return sorted;
    }
  }, [polygonGroups, sortingOption]);

  const renderControls = () => (
    <PolygonPanelControls
        groupingOption={groupingOption}
        onGroupingChange={onGroupingChange}
        sortingOption={sortingOption}
        onSortingChange={onSortingChange}
    />
  );

  if (groupingOption === GroupingOption.NONE) {
    return (
      <SelectionPanel
        items={sortedPolygons}
        selectedIds={selectedPolygons}
        onToggle={onPolygonToggle}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        title={title}
        loading={loading}
        renderItem={(item, isSelected, selectionIndex) => <PolygonItem polygon={item} isSelected={isSelected} selectionIndex={selectionIndex} />}
        renderExtraControls={renderControls}
      />
    );
  }

  // Render group view
  return (
    <SelectionPanel
        items={sortedPolygonGroups}
        selectedIds={selectedGroups}
        onToggle={onGroupToggle}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        title={title}
        loading={loading}
        renderItem={(item, isSelected, selectionIndex) => <PolygonGroupItem group={item} isSelected={isSelected} selectionIndex={selectionIndex} />}
        renderExtraControls={renderControls}
    />
  )
};

export default PolygonSelectionPanel;
